import PhoneInput from "@/components/PhoneInput";
import { Plus, Trash } from "@phosphor-icons/react";
import { validatePhone } from "@/utils/phoneValidation";

/**
 * MultiPhoneInput — dynamic list of phone entries with add/remove.
 *
 * Value shape (array):
 *   [
 *     { number: "+966501234567", is_primary: true,  label: "أساسي" },
 *     { number: "+966501234568", is_primary: false, label: "احتياطي" },
 *   ]
 *
 * Props:
 *   - value: Array<{number, is_primary, label?}> — always at least 1 entry (auto-seeded)
 *   - onChange: (nextArr) => void
 *   - minEntries?: default 1
 *   - maxEntries?: default 5
 *   - testIdPrefix?: string used to build data-testids (default 'multi-phone')
 *   - required?: if true, the primary phone field is marked required
 *   - defaultCountry?: PhoneInput defaultCountry prop (e.g. 'sa')
 */
export default function MultiPhoneInput({
  value,
  onChange,
  minEntries = 1,
  maxEntries = 5,
  testIdPrefix = "multi-phone",
  required = false,
  defaultCountry = "sa",
}) {
  // Ensure we always have at least `minEntries` rows.
  const rows = Array.isArray(value) && value.length > 0
    ? value
    : [{ number: "+966", is_primary: true }];

  const update = (i, next) => {
    const copy = rows.map((r, idx) => (idx === i ? { ...r, ...next } : r));
    onChange(copy);
  };

  const remove = (i) => {
    if (rows.length <= minEntries) return;
    const wasPrimary = rows[i]?.is_primary;
    let copy = rows.filter((_, idx) => idx !== i);
    // If we removed the primary, promote the first remaining row.
    if (wasPrimary && copy.length > 0) {
      copy = copy.map((r, idx) => ({ ...r, is_primary: idx === 0 }));
    }
    onChange(copy);
  };

  const add = () => {
    if (rows.length >= maxEntries) return;
    onChange([...rows, { number: "+966", is_primary: false }]);
  };

  const setPrimary = (i) => {
    const copy = rows.map((r, idx) => ({ ...r, is_primary: idx === i }));
    onChange(copy);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
      {rows.map((row, i) => {
        const isPrimary = !!row.is_primary;
        return (
          <div
            key={i}
            data-testid={`${testIdPrefix}-row-${i}`}
            className="flex items-stretch gap-2"
          >
            <div className="flex-1 min-w-0">
              <PhoneInput
                value={row.number || "+966"}
                onChange={(v) => update(i, { number: v })}
                testId={`${testIdPrefix}-input-${i}`}
                required={required && isPrimary}
                defaultCountry={defaultCountry}
              />
            </div>
            <div className="flex items-center gap-1">
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => setPrimary(i)}
                  disabled={isPrimary}
                  title={isPrimary ? "الرقم الأساسي" : "تعيين كأساسي"}
                  data-testid={`${testIdPrefix}-primary-${i}`}
                  className={`shrink-0 h-11 px-3 rounded-xl text-[11px] border transition-all ${
                    isPrimary
                      ? "bg-primary/20 border-primary text-primary font-bold cursor-default"
                      : "bg-white/5 border-white/20 text-white/60 hover:bg-primary/10 hover:text-primary hover:border-primary/40"
                  }`}
                >
                  {isPrimary ? "★ أساسي" : "☆"}
                </button>
              )}
              {rows.length > minEntries && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  title="حذف"
                  data-testid={`${testIdPrefix}-remove-${i}`}
                  className="shrink-0 w-11 h-11 rounded-xl bg-destructive/10 border border-destructive/40 text-destructive hover:bg-destructive/20 flex items-center justify-center"
                >
                  <Trash size={16} weight="bold" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      {rows.length < maxEntries && (
        <button
          type="button"
          onClick={add}
          data-testid={`${testIdPrefix}-add`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-primary/10 border border-primary/40 text-primary hover:bg-primary/20 font-semibold"
        >
          <Plus size={12} weight="bold" /> إضافة رقم
        </button>
      )}
    </div>
  );
}

/**
 * Helper: given the full "phones" value + a legacy single "phone" fallback,
 * returns a normalised array of at least 1 entry. Useful when hydrating
 * a form from an existing DB record.
 */
export function hydratePhones(phones, legacyPhone) {
  if (Array.isArray(phones) && phones.length > 0) {
    // Ensure exactly one primary.
    const idx = phones.findIndex((p) => p.is_primary);
    if (idx === -1) {
      return phones.map((p, i) => ({ ...p, is_primary: i === 0 }));
    }
    return phones;
  }
  if (legacyPhone) {
    return [{
      number: String(legacyPhone).startsWith("+") ? legacyPhone : `+${legacyPhone}`,
      is_primary: true,
    }];
  }
  return [{ number: "+966", is_primary: true }];
}

/**
 * Serialise: strips empty entries + ensures exactly one primary before persisting.
 * ALSO enforces strict-length validation for KSA/UAE/KW/OM/QA — throws on invalid.
 */
export function serializePhones(rows) {
  const cleaned = (rows || [])
    .map((r) => ({
      number: (r?.number || "").trim(),
      is_primary: !!r?.is_primary,
      label: r?.label || null,
    }))
    // Drop rows with just the country code (+966) or empty.
    .filter((r) => r.number && r.number.replace(/\D/g, "").length >= 8);
  if (cleaned.length === 0) return [];
  const hasPrimary = cleaned.some((r) => r.is_primary);
  if (!hasPrimary) cleaned[0].is_primary = true;
  return cleaned;
}

/**
 * Validate a list of phone rows. Returns the first error message (Arabic)
 * or null when everything is OK. Callers should invoke this BEFORE submit
 * and short-circuit with a toast on failure.
 */
export function validatePhonesList(rows, { requireAtLeastOne = true } = {}) {
  const arr = Array.isArray(rows) ? rows : [];
  const nonEmpty = arr.filter((r) => (r?.number || "").replace(/\D/g, "").length > 3);
  if (requireAtLeastOne && nonEmpty.length === 0) return "رقم الجوال مطلوب";
  for (let i = 0; i < arr.length; i++) {
    const digits = (arr[i]?.number || "").replace(/\D/g, "");
    if (digits.length <= 3) continue; // only country code — skip
    const check = validatePhone(arr[i].number);
    if (!check.ok) return `الرقم ${i + 1}: ${check.reason}`;
  }
  return null;
}
